import { useState, useEffect, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWarehouse,
  faTruckMoving,
  faShoppingCart,
  faFileInvoice,
  faClipboardCheck,
  faFileInvoiceDollar,
  faArrowRight,
  faBoxesStacked,
  faCircleCheck,
  faTriangleExclamation,
  faChartLine,
  faUsers,
  faTableColumns,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { resolveCustomDashboard } from '../config/customDashboards';
import api from '../services/api';
import './Dashboard.css';

function StatCard({ icon, label, value, sub, color, onClick, loading }) {
  return (
    <button
      className={`stat-card ${onClick ? 'stat-card--clickable' : ''}`}
      onClick={onClick}
      style={{ '--card-accent': color }}
      disabled={!onClick}
    >
      <div className="stat-card-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="stat-card-body">
        <div className="stat-card-value">
          {loading ? <span className="stat-skeleton" /> : value ?? '—'}
        </div>
        <div className="stat-card-label">{label}</div>
        {sub && !loading && <div className="stat-card-sub">{sub}</div>}
      </div>
      {onClick && (
        <FontAwesomeIcon icon={faArrowRight} className="stat-card-arrow" />
      )}
    </button>
  );
}

function QuickLink({ icon, label, description, path, color, navigate }) {
  return (
    <button className="quick-link" onClick={() => navigate(path)} style={{ '--ql-color': color }}>
      <div className="quick-link-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="quick-link-body">
        <div className="quick-link-label">{label}</div>
        <div className="quick-link-desc">{description}</div>
      </div>
      <FontAwesomeIcon icon={faArrowRight} className="quick-link-arrow" />
    </button>
  );
}

export default function Dashboard() {
  const { user, hasPermission, permissions } = useAuth();
  const tenant = useTenant();
  const navigate = useNavigate();
  const params = useParams();

  const [kpis,       setKpis]       = useState(null);
  const [pending,    setPending]    = useState(null);
  const [bolCount,   setBolCount]   = useState(null);
  const [wrCount,    setWrCount]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [activeTab,  setActiveTab]  = useState(0); // 0 = Overview, 1+ = custom tabs

  // Resolve the list of custom tabs for this customer (null if none)
  const customTabs = resolveCustomDashboard(user, hasPermission);

  const getTenantLink = (path) => {
    const prefix = params.tenantPrefix;
    return prefix ? `/${prefix}${path}` : path;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const [kpiRes, pendingRes, bolRes, wrRes] = await Promise.all([
        hasPermission('inventory')
          ? api.get('/api/inventory/kpis').catch(() => null)
          : Promise.resolve(null),
        hasPermission('inventory', 'approve')
          ? api.get('/api/inventory/pending-approvals/count').catch(() => null)
          : Promise.resolve(null),
        hasPermission('transportation')
          ? api.get('/api/bols/count').catch(() => null)
          : Promise.resolve(null),
        hasPermission('warehouse_receipts')
          ? api.get('/api/warehouse/receipts/count').catch(() => null)
          : Promise.resolve(null),
      ]);

      setKpis(kpiRes?.data ?? null);
      setPending(pendingRes?.data?.count ?? null);
      setBolCount(bolRes?.data?.count ?? null);
      setWrCount(wrRes?.data?.count ?? null);
      setLoading(false);
    };

    fetchData();
  }, [hasPermission]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const displayName = user?.name || user?.username || 'there';

  return (
    <div className="dashboard">

      {/* Tab bar — only rendered when custom tabs are registered for this customer */}
      {customTabs && (
        <div className="dashboard-tabs" role="tablist">
          <button
            role="tab"
            className={`dashboard-tab${activeTab === 0 ? ' dashboard-tab--active' : ''}`}
            onClick={() => setActiveTab(0)}
            aria-selected={activeTab === 0}
          >
            <FontAwesomeIcon icon={faChartLine} />
            Overview
          </button>
          {customTabs.map((tab, i) => (
            <button
              key={i}
              role="tab"
              className={`dashboard-tab${activeTab === i + 1 ? ' dashboard-tab--active' : ''}`}
              onClick={() => setActiveTab(i + 1)}
              aria-selected={activeTab === i + 1}
            >
              <FontAwesomeIcon icon={tab.icon ?? faTableColumns} />
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* ── CUSTOM DASHBOARD TABS ── */}
      {customTabs && customTabs.map((tab, i) => {
        const TabComponent = tab.component;
        return (
          <div key={i} className={activeTab !== i + 1 ? 'dashboard-tab-panel--hidden' : undefined}>
            <Suspense fallback={
              <div className="custom-dashboard-loading">
                <div className="custom-dashboard-spinner" />
                <span>Loading dashboard…</span>
              </div>
            }>
              <TabComponent
                user={user}
                tenant={tenant}
                getTenantLink={getTenantLink}
                navigate={navigate}
              />
            </Suspense>
          </div>
        );
      })}

      {/* ── OVERVIEW TAB (always rendered when no custom tabs, hidden otherwise) ── */}
      <div className={customTabs && activeTab !== 0 ? 'dashboard-tab-panel--hidden' : undefined}>

      {/* Welcome */}
      <div className="dashboard-welcome">
        <div>
          <h2 className="dashboard-greeting">{greeting()}, {displayName.split(' ')[0]}!</h2>
          <p className="dashboard-subline">
            Here's a snapshot of your logistics portal.
            {user?.customerName && <> &nbsp;·&nbsp; <strong>{user.customerName}</strong></>}
          </p>
        </div>
        {pending > 0 && (
          <button
            className="dashboard-alert-chip"
            onClick={() => navigate(getTenantLink('/pending-approvals'))}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {pending} order{pending !== 1 ? 's' : ''} need approval
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <section className="dashboard-section">
        <h3 className="dashboard-section-title">Overview</h3>
        <div className="stat-grid">
          {hasPermission('transportation') && (
            <StatCard
              icon={faTruckMoving}
              label="Bills of Lading"
              value={bolCount?.toLocaleString()}
              sub="Open shipments"
              color="#8b5cf6"
              loading={loading}
              onClick={() => navigate(getTenantLink('/bols'))}
            />
          )}
          {hasPermission('warehouse_receipts') && (
            <StatCard
              icon={faFileInvoice}
              label="Warehouse Receipts"
              value={wrCount?.toLocaleString()}
              sub="Open WRs"
              color="#06b6d4"
              loading={loading}
              onClick={() => navigate(getTenantLink('/warehouse-receipts'))}
            />
          )}
          {hasPermission('inventory') && (
            <>
              <StatCard
                icon={faBoxesStacked}
                label="Inventory on Hand"
                value={kpis?.totalSKUs?.toLocaleString()}
                sub="Active SKUs"
                color="#3b82f6"
                loading={loading}
                onClick={() => navigate(getTenantLink('/inventory'))}
              />
              <StatCard
                icon={faShoppingCart}
                label="Open Pick Orders"
                value={kpis?.openOrders?.toLocaleString()}
                sub="Pending fulfillment"
                color="#f59e0b"
                loading={loading}
                onClick={() => navigate(getTenantLink('/orders'))}
              />
            </>
          )}
          {hasPermission('inventory', 'approve') && (
            <StatCard
              icon={faClipboardCheck}
              label="Pending Approvals"
              value={pending ?? '—'}
              sub={pending > 0 ? 'Action required' : 'All caught up'}
              color={pending > 0 ? '#ef4444' : '#10b981'}
              loading={pending === null && loading}
              onClick={() => navigate(getTenantLink('/pending-approvals'))}
            />
          )}
        </div>
      </section>

      {/* Quick Actions */}
      <section className="dashboard-section">
        <h3 className="dashboard-section-title">Quick Actions</h3>
        <div className="quick-links-grid">
          {hasPermission('transportation') && (
            <>
              <QuickLink
                icon={faTruckMoving}
                label="Create BOL"
                description="Generate a bill of lading"
                path="/bols"
                color="#8b5cf6"
                navigate={(p) => navigate(getTenantLink(p))}
              />
              <QuickLink
                icon={faChartLine}
                label="Rate Calculator"
                description="Get shipping rates"
                path="/rate-calculator"
                color="#0ea5e9"
                navigate={(p) => navigate(getTenantLink(p))}
              />
            </>
          )}
          {hasPermission('warehouse_receipts') && (
            <QuickLink
              icon={faFileInvoice}
              label="Warehouse Receipts"
              description="View incoming & processed receipts"
              path="/warehouse-receipts"
              color="#06b6d4"
              navigate={(p) => navigate(getTenantLink(p))}
            />
          )}
          {hasPermission('inventory') && (
            <>
              <QuickLink
                icon={faWarehouse}
                label="View Inventory"
                description="Check stock levels by SKU or lot"
                path="/inventory"
                color="#3b82f6"
                navigate={(p) => navigate(getTenantLink(p))}
              />
              {hasPermission('inventory', 'submit') && (
                <QuickLink
                  icon={faShoppingCart}
                  label="New Pick Order"
                  description="Submit a pick or order request"
                  path="/orders"
                  color="#f59e0b"
                  navigate={(p) => navigate(getTenantLink(p))}
                />
              )}
            </>
          )}
          {hasPermission('ext_orders') && (
            <QuickLink
              icon={faCircleCheck}
              label="External Orders"
              description="Review external order requests"
              path="/ext-orders"
              color="#10b981"
              navigate={(p) => navigate(getTenantLink(p))}
            />
          )}
          {hasPermission('accounts') && (
            <QuickLink
              icon={faUsers}
              label="Manage Accounts"
              description="Create and edit sub-accounts"
              path="/accounts"
              color="#ec4899"
              navigate={(p) => navigate(getTenantLink(p))}
            />
          )}
          {permissions?.isAdmin && (
            <QuickLink
              icon={faUsers}
              label="User Management"
              description="Manage portal users and permissions"
              path="/users"
              color="#f97316"
              navigate={(p) => navigate(getTenantLink(p))}
            />
          )}
        </div>
      </section>

      </div>{/* end overview panel */}
    </div>
  );
}
